import type { Msg, Session, SessionStore, User } from '@halo/chat-sdk-react';
import type { ContextMenuItemProps } from '@halo/ui-chat-react';
import { useRef, useCallback, useMemo, Fragment } from 'react';
import {
  MessageDividerLine,
  MessageDividerTime,
  MessageDividerDate,
  ContextMenu,
  CopyIcon,
  DeleteIcon,
  MessageEditIcon,
  MessageForwardIcon,
  MessageReplyIcon,
  tools,
  toaster,
} from '@halo/ui-chat-react';
import {
  isFileMessage,
  isluckyBoxMessage,
  MessageEditStatus,
  isForwardMessage,
  isFailedSentMessage,
  isMyMessage,
  isPendingMessage,
  isSuccessMessage,
  MsgPayloadType,
  isFailedEncryptMessage,
  isStickerMessage,
  isImageMessage,
  isPNGImageMessage,
  AccountType,
  GroupStatusEnum,
} from '@halo/chat-sdk';
import { Box, Center, Flex, Menu, Portal } from '@chakra-ui/react';
import { EnhancedMsgPayloadType, isSessionCategory, SessionCategoryPrefix } from '@/store/client';
import { useActionsStore } from '@/store/actions';
import { useSettingsStore } from '@/store/settings';
import type { DetailDialogRef } from '@/components/Emoji';
import { Emoji } from '@/components/Emoji';
import ReactedIcon from '@/assets/images/message/svg/messageReacted.svg?react';
import { useI18nStore } from '@/store/i18n';
import { RobotName } from '@/components/RobotProfileDialog/Helper';
import { EmojiMenu } from '../ChatContent/EmojiMenu';
import { MsgsItem } from '../messages/msgs-item';

export interface SessionMessagesProps {
  renderFirstMessage?: (key: string) => React.ReactNode;
  sessionStore: SessionStore;
  sendMessageDisabled?: boolean;
}

export function SessionMessages({
  sessionStore,
  renderFirstMessage,
  sendMessageDisabled,
}: SessionMessagesProps) {
  const { t } = useTranslation();
  const { enhancedMessages, session } = sessionStore || {};
  const setDeleteMessagesIsOpen = useActionsStore((store) => store.setDeleteMessagesIsOpen);
  const emojiDetailDialogRef = useRef<DetailDialogRef>(null);
  const sessionStoreRef = useRef<SessionStore>(sessionStore);

  const sessionRef = useRef<Session>(session);

  if (session && !sessionRef.current) {
    sessionRef.current = session;
  }

  sessionStoreRef.current = sessionStore;

  const { editDelayedTime } = useSettingsStore();

  const restrictCopyingDisabled = useMemo(() => {
    return !sessionStore?.groupAccess?.canRestrictCopying;
  }, [sessionStore?.groupAccess?.canRestrictCopying]);

  const restrictForwardDisabled = useMemo(() => {
    return !sessionStore?.groupAccess?.canRestrictForward;
  }, [sessionStore?.groupAccess?.canRestrictForward]);

  const formatTime = useI18nStore((store) => store.formatTime);

  const itemsGetter = useCallback(
    (data: { message: Msg; user: User }) => {
      const { message: msg } = data;
      const sessionStore = sessionStoreRef.current;
      const {
        session,
        user,
        client,
        isGroupAdmin: isAdmin,
        setDraftMessage,
        getFile,
      } = sessionStore;
      // TODO amo: Logic to MsgMenuTrigger
      const isText = MsgPayloadType.TEXT === msg.payload_type;
      const isSticker = isStickerMessage(msg);
      const isFile = isFileMessage(msg);
      const isImage = isImageMessage(msg);
      const isPNGImage = isPNGImageMessage(msg);
      const isLuckyBox = isluckyBoxMessage(msg);
      const isPending = isPendingMessage(msg.status);
      const isSentFailed = isFailedSentMessage(msg.status);
      const isFailedEncryptMsg = isFailedEncryptMessage(msg.status);
      const isSuccess = isSuccessMessage(msg.status);
      const isForward = isForwardMessage(msg.msg_action);
      const isEditing = msg.edit === MessageEditStatus.Editing;

      const isTransfer = msg.payload_type === MsgPayloadType.TRANSFER;
      const isMeMsg = isMyMessage(msg, user?.user_id ?? '');

      const isEditDelayedOver = msg.send_completed_at + editDelayedTime < client?.serverTime.time;

      const isGroup = isSessionCategory(session?.chat_id, SessionCategoryPrefix.Group);
      const isRobot = session?.user_info?.account_type === AccountType.Robot;

      const isMyMsg = isMyMessage(msg, user?.user_id ?? '');
      const expireLabel = tools.expireTimeOptions.findLabelByValue(Number(msg.expire_duration));
      const editedAtText = formatTime(msg.edit_completed_at).hourAndMinute;

      const username = session?.user_info?.username;
      const is_official = username === RobotName.HaloChat;

      const emojiListRaw =
        msg.emoji_reaction
          ?.map((item) => item.reaction_log.map((_) => ({ ..._, emoji_id: item.emoji_id })))
          .flat() || [];
      const emojiList = emojiListRaw
        .sort((a, b) => (b.operation_at ?? 0) - (a.operation_at ?? 0))
        .filter((item, index, self) => {
          return index === self.findIndex((t) => t.user_id === item.user_id);
        });
      const reactionsCount = new Set(emojiListRaw.map((item) => item.user_id)).size;

      const items: ContextMenuItemProps<{ message: Msg; user: User }>[] = [
        emojiList.length > 0
          ? {
              value: 'emoji-detail',
              custom: () => (
                <Flex
                  px="1"
                  py="1.5"
                  gap="2"
                  borderRadius="sm"
                  cursor="pointer"
                  alignItems="center"
                  _hover={{ bg: 'bg.subtle' }}
                  onClick={() => {
                    if (!msg.emoji_reaction) return;
                    emojiDetailDialogRef.current?.open(msg.emoji_reaction);
                  }}
                >
                  <Box flex="0 0 auto">
                    <ReactedIcon />
                  </Box>
                  <Box fontSize="sm" flex="1 1 auto" fontWeight="semibold">
                    {reactionsCount.toLocaleString('en-US')} {t('chat.reactedText')}
                  </Box>
                  <Menu.Root positioning={{ placement: 'right-start', gutter: 2 }}>
                    <Menu.TriggerItem w="auto" flex="0 0 auto">
                      <Emoji.AvatarGroup
                        user_id={user?.user_id}
                        user_ids={emojiList.map((item) => item.user_id)}
                        boxSize="20px"
                        maxDisplay={4}
                        showMini={false}
                      />
                    </Menu.TriggerItem>
                    <Portal>
                      <Menu.Positioner>
                        <Menu.Content
                          backdropFilter="blur(6px)"
                          bg="whiteAlpha.70"
                          maxH="400px"
                          overflowY="auto"
                        >
                          {emojiListRaw.slice(0, 100).map((item) => (
                            <Menu.Item
                              key={`${item.emoji_id}-${item.user_id}`}
                              value={`${item.emoji_id}-${item.user_id}`}
                              borderRadius="sm"
                            >
                              <Emoji.Item data={item} />
                            </Menu.Item>
                          ))}
                        </Menu.Content>
                      </Menu.Positioner>
                    </Portal>
                  </Menu.Root>
                </Flex>
              ),
              hidden: isRobot,
            }
          : { value: 'emoji-detail', label: null, hidden: true },
        {
          value: 'editCompletedAt',
          label: (
            <Box fontWeight="medium" color="fg.muted">
              {t('chat.editedAt', { editedAt: editedAtText })}
            </Box>
          ),
          icon: (
            <Center color="fg.muted">
              <MessageEditIcon />
            </Center>
          ),
          hidden: msg.edit !== MessageEditStatus.Edited || isRobot,
        },
        {
          value: 'reply',
          label: <Box fontWeight="semibold">{t('action.reply')}</Box>,
          icon: <MessageReplyIcon />,
          hidden: !isSuccess || isRobot || sendMessageDisabled,
          onItemClick: () => {
            if (isGroup && sessionStore?.groupInfo?.status === GroupStatusEnum.BAN) {
              toaster.error({ title: t('chat.groupBanned') });
              return;
            }
            setDraftMessage({
              isEditing: false,
              replyId: msg.cli_msg_id,
            });
          },
        },
        {
          value: 'forward',
          label: <Box fontWeight="semibold">{t('action.forward')}</Box>,
          icon: <MessageForwardIcon />,
          hidden:
            !isSuccess ||
            isTransfer ||
            isLuckyBox ||
            restrictForwardDisabled ||
            sendMessageDisabled ||
            (isRobot && !is_official),
          onItemClick: () => {
            setDraftMessage({
              forwardId: msg.cli_msg_id,
            });
          },
        },
        {
          value: 'copy',
          label: <Box fontWeight="semibold">{t('action.copy')}</Box>,
          icon: <CopyIcon />,
          hidden:
            !isSuccess ||
            (isFile && (!isImage || !isPNGImage)) ||
            isLuckyBox ||
            isTransfer ||
            isFailedEncryptMsg ||
            isSticker ||
            restrictCopyingDisabled,
          onItemClick: async () => {
            if (isImage) {
              const file = await getFile(msg);
              if (!file) return;
              await tools.copyFileToClipboard(async () =>
                fetch(file)
                  .then((response) => response.blob())
                  .then((blob) => {
                    return new File([blob], msg.payload.name, { type: msg.payload.type });
                  })
              );
            } else {
              tools.copyToClipboard(msg.payload.content);
            }
          },
        },
        {
          value: 'edit',
          label: <Box fontWeight="semibold">{t('action.edit')}</Box>,
          icon: <MessageEditIcon />,
          hidden:
            !isSuccess ||
            !isText ||
            isEditDelayedOver ||
            !isMeMsg ||
            isEditing ||
            isForward ||
            isRobot ||
            sendMessageDisabled,
          onItemClick: () => {
            if (isGroup && sessionStore?.groupInfo?.status === GroupStatusEnum.BAN) {
              toaster.error({ title: t('chat.groupBanned') });
              return;
            }
            setDraftMessage({
              isEditing: true,
              replyId: msg.cli_msg_id,
            });
          },
        },
        {
          value: 'delete',
          label: <Box fontWeight="semibold">{t('action.delete')}</Box>,
          description:
            isRobot || msg.expire_duration === 0 ? null : (
              <Box>
                {t('chat.autoDelete')} {expireLabel}
              </Box>
            ),
          danger: true,
          icon: <DeleteIcon />,
          hidden: isPending,
          onItemClick: () => {
            setDeleteMessagesIsOpen(
              true,
              session,
              [msg],
              !isSentFailed && (isMyMsg || !isGroup || isAdmin),
              function deleteMessagesOnDelete() {
                return new Promise((resolve) => {
                  resolve(true);
                });
              }
            );
          },
        },
        {
          value: 'emoji-panel',
          custom: (data: any, actions: any) => <EmojiMenu data={data} actions={actions} />,
          isTopToolbar: true,
          hidden: !isSuccessMessage(data?.message?.status) || isRobot || sendMessageDisabled,
        },
      ].filter((item) => !item.hidden);
      return items;
    },
    [
      editDelayedTime,
      restrictForwardDisabled,
      restrictCopyingDisabled,
      setDeleteMessagesIsOpen,
      t,
      formatTime,
      sendMessageDisabled,
    ]
  );

  return (
    <ContextMenu<{ message: Msg; user: User }>
      itemsGetter={itemsGetter}
      contentProps={{
        borderRadius: 'lg',
        minW: '260px',
      }}
      rootProps={{
        positioning: {
          offset: {
            mainAxis: -48,
          },
        },
      }}
    >
      {enhancedMessages?.map((message) => {
        const createdAtDisplay = message.created_at ? formatTime(message.created_at) : undefined;
        switch (message.payload_type) {
          case EnhancedMsgPayloadType.FirstMsg:
            return (
              <Fragment key={message.cli_msg_id}>
                {renderFirstMessage?.(message.cli_msg_id)}
                {/* To solve the problem where the first time slice disappears when scrolling to the top */}
                <div className="MessageDividerDate" style={{ marginTop: '-20px' }}></div>
                <div className="MessageItem" style={{ marginTop: '-20px' }}></div>
              </Fragment>
            );
          case EnhancedMsgPayloadType.LastMsg:
            return null;
          case EnhancedMsgPayloadType.UnreadSplitMsg:
            return (
              <MessageDividerLine key={message.cli_msg_id}>
                {t('chat.newMessages')}
              </MessageDividerLine>
            );
          case EnhancedMsgPayloadType.DateSplitMsg:
            return (
              <MessageDividerDate key={message.cli_msg_id}>
                {createdAtDisplay?.title}
              </MessageDividerDate>
            );
          case EnhancedMsgPayloadType.TodaySplitMsg:
            return (
              <MessageDividerTime key={message.cli_msg_id}>
                {createdAtDisplay?.hourAndMinute}
              </MessageDividerTime>
            );
        }

        return (
          <MsgsItem key={message.cli_msg_id} message={message} session={sessionRef.current!} />
        );
      })}
      <Emoji.DetailDialog ref={emojiDetailDialogRef} />
    </ContextMenu>
  );
}
